import { useRef, useState, useEffect } from 'react';
import { apiJSONURL } from '../../App';
import { encrypt } from '../data/CryptoUtil';
import { useGlobalContext } from '../data/GlobalContext';
import { channels } from '../data/Payment';
import PaymentSelection from './PaymentSelection';
import { ChoosePaymentMethod } from './PaymentSelection';
import { EditPaymentForm } from './PaymentSelection';
import {
    EwalletForm,
    BankForm,
    PaymentWalletForm,
    CryptoPaymentForm,
} from './PaymentSelection';
export default function Withdraw({ onClose }) {
    const {
        username,
        playerDetails,
        rollover,
        sg8Wallet,
        refreshWalletBalances,
        applyWalletBalances,
    } = useGlobalContext();
    // const { username, playerDetails, rollover, sg8Wallet } = useGlobalContext()
    const input1Ref = useRef(null);
    const inputARef = useRef(null);
    const inputBRef = useRef(null);
    const inputCRef = useRef(null);
    const isSubmittingRef = useRef(false);

    const [activeSection, setActiveSection] = useState(null);
    const [bankInput, setBankInput] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [verified, setVerified] = useState(false);
    const [attemptCount, setAttemptCount] = useState(0);
    const [isLocked, setIsLocked] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [inputError, setInputError] = useState(false);
    const [, setSectionHistory] = useState([]);
    const [selectedBank, setSelectedBank] = useState(null);
    // const [withdrawType, setDepositType] = useState("")
    const [withdrawType, setWithdrawType] = useState('');
    const [searchBank, setSearchBank] = useState('');
    const frequentlyUsedNames = ['GCash', 'Maya'];
    const [accountNumber, setAccountNumber] = useState('');
    const [accountName, setAccountName] = useState('');
    const [walletAddress, setWalletAddress] = useState('');

    // Additional states for processing and alert messages
    const [processing, setProcessing] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [editingMethod, setEditingMethod] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [alertType, setAlertType] = useState('');
    const [paymentRefreshKey, setPaymentRefreshKey] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [cooldownUntil, setCooldownUntil] = useState(0);

    const alertTimeoutRef = useRef(null);

    const ADD_PAYMENT = 'add_payment';
    const EDIT_PAYMENT = 'EDIT_PAYMENT';
    const CHOOSE_PAYMENT = 'choose_payment';

    const fillPaymentFields = (method, type) => {
        if (!method) {
            setAccountNumber('');
            setAccountName('');
            setWalletAddress('');
            return;
        }

        if (type === 'bank') {
            setAccountName(method.name || '');
            setAccountNumber(method.value || '');
            setWalletAddress('');
        } else if (type === 'crypto') {
            setWalletAddress(method.value || '');
            setAccountName(method.name || '');
            setAccountNumber('');
        } else {
            setAccountName(method.name || '');
            setAccountNumber(method.value || '');
            setWalletAddress('');
        }
    };

    const handlePaymentSelect = (method) => {
        setSelectedPayment(method);

        const { category, channel } = resolvePaymentCategory(method?.type);

        setWithdrawType(category);
        setSelectedBank(channel || null);

        fillPaymentFields(method, category);
    };

    const normalizePaymentType = (type) => {
        if (type === 'banks') return 'bank';
        if (type === 'ewallets') return 'ewallet';
        return type;
    };

    const findChannelMeta = (account) => {
        const accountType = (account?.type || '').toLowerCase();

        const allChannels = [
            ...(channels.ewallet || []),
            ...(channels.bank || []),
            ...(channels.crypto || []),
        ];

        return (
            allChannels.find(
                (item) =>
                    item.mbc === account.type ||
                    item.name?.toLowerCase() === accountType
            ) || null
        );
    };

    const resolvePaymentCategory = (accountType) => {
        const typeValue = String(accountType || '')
            .toLowerCase()
            .trim();

        const matchedEwallet = (channels.ewallet || []).find(
            (item) =>
                item.mbc?.toLowerCase() === typeValue ||
                item.name?.toLowerCase() === typeValue
        );
        if (matchedEwallet) {
            return { category: 'ewallet', channel: matchedEwallet };
        }

        const matchedBank = (channels.bank || []).find(
            (item) =>
                item.mbc?.toLowerCase() === typeValue ||
                item.name?.toLowerCase() === typeValue
        );
        if (matchedBank) {
            return { category: 'bank', channel: matchedBank };
        }

        const matchedCrypto = (channels.crypto || []).find(
            (item) =>
                item.mbc?.toLowerCase() === typeValue ||
                item.name?.toLowerCase() === typeValue
        );
        if (matchedCrypto) {
            return { category: 'crypto', channel: matchedCrypto };
        }

        return { category: 'ewallet', channel: null };
    };

    const handleChoosePaymentSelect = (method) => {
        setSelectedPayment(method);

        const { category, channel } = resolvePaymentCategory(method?.type);

        setWithdrawType(category);

        if (channel) {
            setSelectedBank(channel);
        } else {
            setSelectedBank(null);
        }

        fillPaymentFields(method, category);
        setActiveSection('open_bank');
    };

    // Helper to display messages with a timeout
    const showMessageWithTimeout = (
        message,
        type = 'error',
        duration = 30000
    ) => {
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }

        setAlertMessage(message);
        setAlertType(type);

        if (type === 'success') {
            setIsSubmitted(true);
        }

        alertTimeoutRef.current = setTimeout(() => {
            setAlertMessage('');
            setAlertType('');
            setIsSubmitted(false);
        }, duration);
    };

    // Lock scroll when in a sub-section
    useEffect(() => {
        const container = document.querySelector(
            '.wallet-transaction-container'
        );
        if (container)
            container.style.overflow = activeSection ? 'hidden' : 'auto';
        return () => container && (container.style.overflow = 'auto');
    }, [activeSection]);

    // Returns header info based on active section and selected bank/wallet
    const getHeaderInfo = (section, selectedBank) => {
        const headers = {
            bank_selection: {
                header: 'Bank Account',
                subheader: 'Select a Bank',
            },
            ewallet_selection: {
                header: 'E-Wallet Account',
                subheader: 'Choose Wallet',
            },
            crypto_selection: {
                header: 'Crypto Currency',
                subheader: 'Choose Currency',
            },
            otc_selection: { header: 'Over the Counter', subheader: null },
            open_bank: {
                header: selectedBank
                    ? `Cash out from ${selectedBank.name}`
                    : '',
                subheader: null,
            },
            promotion: {
                header: 'Apply Promotion',
                subheader: 'Select a promotion',
            },
        };
        return headers[section] || { header: '', subheader: '' };
    };

    // Helper to generate dynamic lock message
    const updateLockMessage = (time) =>
        `Too many failed attempts. Please wait ${time} second${
            time !== 1 ? 's' : ''
        } before trying again.`;

    // On mount, retrieve stored attempt count and lock info from local storage
    useEffect(() => {
        const storedAttemptCount = localStorage.getItem('withdrawAttemptCount');
        if (storedAttemptCount) {
            setAttemptCount(parseInt(storedAttemptCount, 10));
        }
        const lockInfo = localStorage.getItem('withdrawLock');
        if (lockInfo) {
            const parsedLock = JSON.parse(lockInfo);
            // If lock is still active
            if (parsedLock.locked && parsedLock.expires > Date.now()) {
                setIsLocked(true);
                const remainingTime = parsedLock.expires - Date.now();
                const seconds = Math.floor(remainingTime / 1000);
                setTimeLeft(seconds);
                setErrorMsg(updateLockMessage(seconds));

                const interval = setInterval(() => {
                    setTimeLeft((prev) => {
                        const newTime = prev - 1;
                        setErrorMsg(updateLockMessage(newTime));
                        if (newTime <= 0) {
                            clearInterval(interval);
                            return 0;
                        }
                        return newTime;
                    });
                }, 1000);

                setTimeout(() => {
                    setIsLocked(false);
                    setAttemptCount(0);
                    setErrorMsg('');
                    localStorage.removeItem('withdrawLock');
                    localStorage.setItem('withdrawAttemptCount', '0');
                    clearInterval(interval);
                }, remainingTime);
            } else {
                localStorage.removeItem('withdrawLock');
            }
        }
    }, []);

    const moveOnMax = (e, nextRef) => {
        if (e.target.value.length === 1 && nextRef.current) {
            nextRef.current.focus();
        }
    };

    // Withdraw limits by type
    const withdrawLimits = {
        crypto: { min: 200, max: Infinity },
        bank: { min: 100, max: 300000 },
        ewallet: { min: 200, max: 50000 },
    };

    const formatCurrency = (value) => {
        const num = parseInt(value.replace(/[^\d]/g, ''), 10);
        return !isNaN(num) ? `₱ ${num.toLocaleString('en-US')}` : '';
    };
    const parseCurrency = (value) => parseInt(value.replace(/[^\d]/g, ''), 10);

    const handleInputChange = (e) => {
        const formatted = formatCurrency(e.target.value);
        setBankInput(formatted);
        const numericValue = parseCurrency(formatted);
        const { min, max } = withdrawLimits[withdrawType] || {};
        setInputError(
            !isNaN(numericValue) && (numericValue < min || numericValue > max)
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Combine values from the inputs
        const enteredPin =
            (input1Ref.current.value || '') +
            (inputARef.current.value || '') +
            (inputBRef.current.value || '') +
            (inputCRef.current.value || '');

        if (enteredPin.length < 4) {
            setErrorMsg('Please enter all 4 digits.');
            setTimeout(() => setErrorMsg(''), 5000);
            return;
        }
        if (isLocked) {
            setErrorMsg(updateLockMessage(timeLeft));
            return;
        }
        if (enteredPin !== playerDetails.pin) {
            // Update attempt count and store it in local storage
            setAttemptCount((prev) => {
                const newCount = prev + 1;
                localStorage.setItem(
                    'withdrawAttemptCount',
                    newCount.toString()
                );
                if (newCount >= 3) {
                    setIsLocked(true);
                    const lockDuration = 1 * 60 * 1000; // 1 minute
                    const initialSeconds = lockDuration / 1000;
                    setTimeLeft(initialSeconds);
                    setErrorMsg(updateLockMessage(initialSeconds));

                    // Save lock info to local storage
                    const lockData = {
                        locked: true,
                        expires: Date.now() + lockDuration,
                        message: updateLockMessage(initialSeconds),
                    };
                    localStorage.setItem(
                        'withdrawLock',
                        JSON.stringify(lockData)
                    );

                    const interval = setInterval(() => {
                        setTimeLeft((prev) => {
                            const newTime = prev - 1;
                            setErrorMsg(updateLockMessage(newTime));
                            if (newTime <= 0) {
                                clearInterval(interval);
                                return 0;
                            }
                            return newTime;
                        });
                    }, 1000);

                    setTimeout(() => {
                        setIsLocked(false);
                        setAttemptCount(0);
                        setErrorMsg('');
                        localStorage.removeItem('withdrawLock');
                        localStorage.setItem('withdrawAttemptCount', '0');
                        clearInterval(interval);
                    }, lockDuration);
                } else {
                    setErrorMsg(
                        `Withdrawal PIN is incorrect. You have ${3 - newCount} attempt${
                            3 - newCount !== 1 ? 's' : ''
                        } remaining.`
                    );
                }
                return newCount;
            });

            // Clear inputs after a failed attempt
            input1Ref.current.value = '';
            inputARef.current.value = '';
            inputBRef.current.value = '';
            inputCRef.current.value = '';
            return;
        }

        setErrorMsg('');
        setVerified(true);
        localStorage.setItem('withdrawAttemptCount', '0');
    };

    const depositAmount = parseCurrency(bankInput);
    // const limits = withdrawLimits[withdrawType]

    const isSubmitDisabled =
        // !limits || // 👈 critical fix
        !bankInput ||
        isNaN(depositAmount) ||
        depositAmount < withdrawLimits[withdrawType].min ||
        depositAmount > withdrawLimits[withdrawType].max ||
        // depositAmount < limits.min ||
        // depositAmount > limits.max ||
        (withdrawType === 'ewallet' && !accountNumber.trim()) ||
        (withdrawType === 'bank' &&
            (!accountName.trim() || !accountNumber.trim())) ||
        (withdrawType === 'crypto' && !walletAddress.trim());

    const DepositSelectionTools = ({ onBack, header, subheader }) => (
        <div className="deposit-selection-tools">
            <button onClick={onBack} className="transaction-back-btn">
                <img src="/images/icon/black-left-arrow.webp" alt="Back" />
            </button>
            <label>{header}</label>
            {subheader && (
                <label className="withdraw-selection-channel-sublabel">
                    {subheader}
                </label>
            )}
        </div>
    );
    // Navigation functions
    const handleChangeSection = (newSection) => {
        setBankInput('');
        setInputError(false);
        setAlertMessage('');
        setProcessing(false);
        setIsSubmitted(false);

        // only clear fields when leaving the form flow if you really want
        if (newSection !== 'open_bank') {
            setAccountNumber('');
            setAccountName('');
            setWalletAddress('');
            setSelectedPayment(null);
        }

        setSectionHistory((prev) =>
            activeSection ? [...prev, activeSection] : prev
        );
        setActiveSection(newSection);
    };

    const handleBack = () => {
        setInputError(false);
        setBankInput('');
        setAccountNumber('');
        setAccountName('');
        setWalletAddress('');
        setAlertMessage('');
        setProcessing(false);
        setIsSubmitted(false);

        setInputError(false);
        if (activeSection !== 'promotion') {
            setBankInput('');
        }
        setSectionHistory((prev) => {
            if (prev.length) {
                const lastSection = prev[prev.length - 1];
                setActiveSection(lastSection);
                return prev.slice(0, -1);
            }
            setActiveSection(null);
            return [];
        });
    };

    const submitFormHandler = async (e) => {
        e.preventDefault();

        if (Date.now() < cooldownUntil) {
            showMessageWithTimeout(
                'Please wait before submitting another withdrawal.'
            );
            return;
        }

        if (isSubmittingRef.current || processing || isSubmitted) return;

        isSubmittingRef.current = true;
        setProcessing(true);
        setCooldownUntil(Date.now() + 15000);

        try {
            const encryptedUsername = await encrypt(username);

            const formData = new FormData(e.target);
            const rawAmtValue = formData.get('amt') || '';
            const numericValue = parseCurrency(rawAmtValue);
            const currencyBalance = parseFloat(
                String(sg8Wallet || '0').replace(/,/g, '')
            );

            formData.set('amt', numericValue.toString());
            formData.set('u', encryptedUsername);

            if (numericValue > currencyBalance) {
                showMessageWithTimeout(
                    'Withdrawal amount exceeds available balance.'
                );
                return;
            }

            const response = await fetch(apiJSONURL, {
                method: 'POST',
                body: new URLSearchParams(formData),
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });

            const data = await response.json();

            if (data.s) {
                setIsSubmitted(true);

                if (username) {
                    await refreshWalletBalances(username);
                }

                if (
                    data.balance !== undefined ||
                    data.SG8WALLET !== undefined ||
                    data.ASC !== undefined ||
                    data.GOLDENF !== undefined ||
                    data.SBSBO !== undefined ||
                    data.RTG !== undefined
                ) {
                    applyWalletBalances(data);
                }

                showMessageWithTimeout(
                    <div className="success-ticket">
                        <p className="title-ticket">
                            🎟️ Withdrawal Ticket <strong>#{data.id}</strong>{' '}
                            Received!
                        </p>
                        <br />
                        <p className="verified">
                            ✔️ Verified account? Auto-processed
                        </p>
                        <p className="unverified">
                            ⛔ Unverified/1st-time user?{' '}
                            <strong>
                                <a href="/#contactUs">to verify</a>
                            </strong>
                        </p>
                        <br />
                        <p className="reminder">
                            🔔 Check your bank or e-wallet within{' '}
                            <strong>20-30 mins</strong>. If funds are returned
                            to your FGH account, use another verified account &
                            re-submit.
                        </p>
                    </div>,
                    'success'
                );

                setBankInput('');
            } else {
                showMessageWithTimeout(data.emsg || 'Withdrawal failed.');
            }
        } catch (error) {
            showMessageWithTimeout('An error occurred, please try again.');
        } finally {
            setProcessing(false);
            isSubmittingRef.current = false;
        }
    };

    const submitHandlerMapping = {
        handleGcashSubmit: submitFormHandler,
        handleMayaSubmit: submitFormHandler,
        handleFortunePaySubmit: submitFormHandler,
        handleCryptoSubmit: submitFormHandler,
        handleBankSubmit: submitFormHandler,
    };

    const handleEwalletSubmit = (e) => {
        e.preventDefault();
        if (selectedBank && selectedBank.onSubmit) {
            const submitHandler = submitHandlerMapping[selectedBank.onSubmit];
            if (submitHandler) {
                submitHandler(e);
            } else {
                console.error(
                    'Submit handler not found for',
                    selectedBank.onSubmit
                );
            }
        }
    };

    const moveOnBackspace = (e, prevRef) => {
        if (
            e.key === 'Backspace' &&
            e.target.value === '' &&
            prevRef?.current
        ) {
            prevRef.current.focus();
        }
    };

    const handleAddPaymentSuccess = (newMethod) => {
        const { category, channel } = resolvePaymentCategory(newMethod?.type);

        setSelectedPayment(newMethod);
        setWithdrawType(category);
        setSelectedBank(channel || null);
        fillPaymentFields(newMethod, category);

        setPaymentRefreshKey((k) => k + 1);
        setActiveSection('open_bank');
    };

    const handleUnlinkPaymentSuccess = (removedMethod) => {
        if (selectedPayment?.id === removedMethod?.id) {
            setSelectedPayment(null);
            setSelectedBank(null);
            setWithdrawType('');
            fillPaymentFields(null);
        }

        setPaymentRefreshKey((k) => k + 1);
        setActiveSection(null);
    };

    useEffect(() => {
        fillPaymentFields(selectedPayment, withdrawType);
    }, [selectedPayment, withdrawType]);

    useEffect(() => {
        return () => {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!username || !isSubmitted) return;

        const interval = setInterval(() => {
            refreshWalletBalances(username);
        }, 5000);

        return () => clearInterval(interval);
    }, [username, isSubmitted, refreshWalletBalances]);

    return (
        <>
            <div className="wallet-transaction-container">
                <div className="withdrawal-container">
                    <div className="withdraw-selection-channel">
                        <button
                            className="transaction-global-close-btn"
                            onClick={() => {
                                setBankInput('');
                                setInputError(false);
                                onClose && onClose();
                            }}
                        >
                            <img
                                src="/images/icon/white-close-btn.webp"
                                alt="Close"
                            />
                        </button>
                        <label>Cashout</label>

                        <PaymentSelection
                            key={`payment-${paymentRefreshKey}`}
                            selected={selectedPayment?.id}
                            onSelect={handleChoosePaymentSelect}
                            onAddNew={() => handleChangeSection(CHOOSE_PAYMENT)}
                            methods={paymentMethods}
                            onEditPayment={(method) => {
                                setEditingMethod(method);
                                setActiveSection(EDIT_PAYMENT);
                            }}
                        />

                        <div className="transaction-label-notice">
                            <label>
                                <img
                                    src="/images/icon/notice-icon.webp"
                                    alt=""
                                    className="notice-icon"
                                />{' '}
                                Only use our official withdrawal channels
                            </label>
                            <p>
                                For withdrawals, all transactions must be
                                processed exclusively through our official
                                automated payment system on our site.{' '}
                                <strong>Beware of scammers!</strong> We will
                                never ask you to withdraw funds outside of these
                                secure channels. If you receive any suspicious
                                requests, please report them immediately.
                            </p>
                        </div>
                    </div>

                    {activeSection && (
                        <div className="withdraw-channel-content">
                            <button
                                className="transaction-global-close-btn"
                                onClick={() => {
                                    setBankInput('');
                                    setInputError(false);
                                    onClose && onClose();
                                }}
                            >
                                <img
                                    src="/images/icon/white-close-btn.webp"
                                    alt="Close"
                                />
                            </button>

                            <DepositSelectionTools
                                onBack={handleBack}
                                {...getHeaderInfo(activeSection, selectedBank)}
                            />

                            {activeSection === ADD_PAYMENT && (
                                <>
                                    {selectedType === 'ewallets' &&
                                        selectedLabel !== 'E-Wallet' && (
                                            <EwalletForm
                                                selectedType={selectedType}
                                                selectedLabel={selectedLabel}
                                                onAdd={handleAddPaymentSuccess}
                                            />
                                        )}

                                    {selectedType === 'bank' && (
                                        <BankForm
                                            onAdd={handleAddPaymentSuccess}
                                            onCancel={handleBack}
                                        />
                                    )}

                                    {selectedType === 'ewallet' && (
                                        <PaymentWalletForm
                                            selectedLabel={selectedLabel}
                                            onAdd={handleAddPaymentSuccess}
                                            onCancel={handleBack}
                                        />
                                    )}

                                    {selectedType === 'crypto' && (
                                        <CryptoPaymentForm
                                            selectedLabel={selectedLabel}
                                            onAdd={handleAddPaymentSuccess}
                                            onCancel={handleBack}
                                        />
                                    )}
                                </>
                            )}

                            {activeSection === EDIT_PAYMENT &&
                                editingMethod && (
                                    <EditPaymentForm
                                        method={editingMethod}
                                        onCancel={() =>
                                            setActiveSection('open_bank')
                                        }
                                        onSave={(updated) => {
                                            setPaymentMethods((prev) =>
                                                (prev || []).map((m) => {
                                                    if (updated.isDefault) {
                                                        return {
                                                            ...(m.id ===
                                                            updated.id
                                                                ? {
                                                                      ...m,
                                                                      ...updated,
                                                                  }
                                                                : m),
                                                            isDefault:
                                                                m.id ===
                                                                updated.id
                                                                    ? 1
                                                                    : 0,
                                                        };
                                                    }

                                                    return m.id === updated.id
                                                        ? { ...m, ...updated }
                                                        : m;
                                                })
                                            );

                                            setSelectedPayment(updated);
                                            setPaymentRefreshKey((k) => k + 1);
                                            setActiveSection('open_bank');
                                        }}
                                        onUnlink={handleUnlinkPaymentSuccess}
                                    />
                                )}

                            {activeSection === CHOOSE_PAYMENT && (
                                <ChoosePaymentMethod
                                    onSelectType={(type) => {
                                        setSelectedType(
                                            normalizePaymentType(type)
                                        );
                                        handleChangeSection(ADD_PAYMENT);
                                    }}
                                    onSelectLabel={(label) =>
                                        setSelectedLabel(label)
                                    }
                                />
                            )}

                            {activeSection === 'bank_selection' && (
                                <>
                                    <input
                                        type="text"
                                        className="bank-search-input"
                                        placeholder="Search bank..."
                                        value={searchBank}
                                        onChange={(e) =>
                                            setSearchBank(e.target.value)
                                        }
                                    />

                                    {/* Frequently Used Banks Section */}
                                    {searchBank.trim() === '' && (
                                        <div
                                            className="frequent-banks"
                                            style={{ marginBottom: '15px' }}
                                        >
                                            <label
                                                style={{
                                                    fontWeight: 'bold',
                                                    marginBottom: '8px',
                                                    display: 'block',
                                                }}
                                            >
                                                Frequently Used
                                            </label>
                                            <ul className="withdraw-item-selection">
                                                {frequentlyUsedNames
                                                    .map((name) =>
                                                        channels.bank.find(
                                                            (b) =>
                                                                b.name
                                                                    .toLowerCase()
                                                                    .replace(
                                                                        /\s+/g,
                                                                        ''
                                                                    ) ===
                                                                name
                                                                    .toLowerCase()
                                                                    .replace(
                                                                        /\s+/g,
                                                                        ''
                                                                    )
                                                        )
                                                    )
                                                    .filter(Boolean)
                                                    .map((data) => (
                                                        <li
                                                            key={`frequent-${data.id}`}
                                                            onClick={() => {
                                                                setSelectedBank(
                                                                    data
                                                                );
                                                                setWithdrawType(
                                                                    'bank'
                                                                );
                                                                handleChangeSection(
                                                                    'open_bank'
                                                                );
                                                            }}
                                                        >
                                                            <img
                                                                src={data.logo}
                                                                alt={data.name}
                                                            />{' '}
                                                            {data.name}
                                                        </li>
                                                    ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Search Results List */}
                                    <ul className="withdraw-item-selection">
                                        {(() => {
                                            const filteredBanks = Array.from(
                                                new Map(
                                                    channels.bank.map(
                                                        (item) => [
                                                            item.name.toLowerCase(),
                                                            item,
                                                        ]
                                                    )
                                                ).values()
                                            )
                                                .filter((bank) =>
                                                    bank.name
                                                        .toLowerCase()
                                                        .replace(/\s+/g, '')
                                                        .includes(
                                                            searchBank
                                                                .toLowerCase()
                                                                .replace(
                                                                    /\s+/g,
                                                                    ''
                                                                )
                                                        )
                                                )
                                                .filter((bank) => {
                                                    if (
                                                        searchBank.trim() === ''
                                                    ) {
                                                        // No search? Exclude GCash, Maya, Shopee
                                                        return !frequentlyUsedNames.some(
                                                            (name) =>
                                                                name
                                                                    .toLowerCase()
                                                                    .replace(
                                                                        /\s+/g,
                                                                        ''
                                                                    ) ===
                                                                bank.name
                                                                    .toLowerCase()
                                                                    .replace(
                                                                        /\s+/g,
                                                                        ''
                                                                    )
                                                        );
                                                    }
                                                    // Searching? Show all
                                                    return true;
                                                });

                                            if (filteredBanks.length === 0) {
                                                return (
                                                    <li
                                                        style={{
                                                            textAlign: 'center',
                                                            padding: '10px',
                                                            color: '#999',
                                                            listStyle: 'none',
                                                        }}
                                                    >
                                                        No Data Found
                                                    </li>
                                                );
                                            }

                                            return filteredBanks.map((data) => (
                                                <li
                                                    key={data.id}
                                                    onClick={() => {
                                                        setSelectedBank(data);
                                                        setWithdrawType('bank');
                                                        handleChangeSection(
                                                            'open_bank'
                                                        );
                                                    }}
                                                >
                                                    <img
                                                        src={data.logo}
                                                        alt={data.name}
                                                    />{' '}
                                                    {data.name}
                                                </li>
                                            ));
                                        })()}
                                    </ul>
                                </>
                            )}

                            {activeSection === 'ewallet_selection' && (
                                <>
                                    <ul className="withdraw-item-selection">
                                        {channels.ewallet
                                            // Filter out the QRPH item
                                            .filter(
                                                (item) => item.name !== 'QRPH'
                                            )
                                            .map((data) => (
                                                <li
                                                    key={data.id}
                                                    onClick={() => {
                                                        setSelectedBank(data);
                                                        setWithdrawType(
                                                            'ewallet'
                                                        );
                                                        handleChangeSection(
                                                            'open_bank'
                                                        );
                                                    }}
                                                >
                                                    <img
                                                        src={data.logo}
                                                        alt={data.name}
                                                    />{' '}
                                                    {data.name}
                                                </li>
                                            ))}
                                    </ul>
                                </>
                            )}

                            {activeSection === 'crypto_selection' && (
                                <ul className="withdraw-item-selection">
                                    {channels.crypto
                                        // Filter out everything except TRC20-USDT
                                        .filter(
                                            (item) => item.name === 'TRC20-USDT'
                                        )
                                        .map((data) => (
                                            <li
                                                key={data.id}
                                                onClick={() => {
                                                    setSelectedBank(data);
                                                    setWithdrawType('crypto');
                                                    handleChangeSection(
                                                        'open_bank'
                                                    );
                                                }}
                                            >
                                                <img
                                                    src={data.logo}
                                                    alt={data.name}
                                                />{' '}
                                                {data.name}
                                            </li>
                                        ))}
                                </ul>
                            )}

                            {activeSection === 'otc_selection' && (
                                <div>
                                    <p>Over the Counter content</p>
                                </div>
                            )}

                            {activeSection === 'open_bank' &&
                                selectedBank &&
                                withdrawType === 'ewallet' && (
                                    <div>
                                        {/* <div className="form-wallet-balance">
                          Available Balance: <strong>{sg8Wallet}</strong>
                        </div> */}
                                        <PaymentSelection
                                            filterType={withdrawType}
                                            selected={selectedPayment?.id}
                                            onSelect={handlePaymentSelect}
                                            onAddNew={() =>
                                                handleChangeSection(
                                                    CHOOSE_PAYMENT
                                                )
                                            }
                                            methods={paymentMethods}
                                            onEditPayment={(method) => {
                                                setEditingMethod(method);
                                                setActiveSection(EDIT_PAYMENT);
                                            }}
                                        />

                                        <form
                                            onSubmit={handleEwalletSubmit}
                                            className="transaction-form"
                                            encType="multipart/form-data"
                                        >
                                            <input
                                                name="cmd"
                                                type="hidden"
                                                value={selectedBank?.cmd || ''}
                                            />
                                            {selectedBank?.name ===
                                                'FortunePay' && (
                                                <input
                                                    name="method"
                                                    type="hidden"
                                                    value={
                                                        selectedBank?.method2 ||
                                                        ''
                                                    }
                                                />
                                            )}
                                            {selectedBank?.name !==
                                                'FortunePay' && (
                                                <input
                                                    name="method"
                                                    type="hidden"
                                                    value={
                                                        selectedBank?.method ||
                                                        ''
                                                    }
                                                />
                                            )}
                                            <input
                                                name="mbc"
                                                type="hidden"
                                                value={selectedBank?.mbc || ''}
                                            />
                                            <input
                                                name="man"
                                                type="hidden"
                                                value={playerDetails.playername}
                                            />
                                            {/* <input name="man" type="hidden" value={accountName} /> */}
                                            <div
                                                className={`transaction-field-withdraw ${
                                                    inputError ? 'error' : ''
                                                }`}
                                                style={{ marginBottom: '10px' }}
                                            >
                                                <label>Account Number</label>
                                                <input
                                                    type="number"
                                                    inputMode="numeric"
                                                    min={0}
                                                    placeholder="Enter account number"
                                                    name="mac"
                                                    value={accountNumber}
                                                    onChange={(e) =>
                                                        setAccountNumber(
                                                            e.target.value
                                                        )
                                                    }
                                                    readOnly
                                                />
                                            </div>
                                            <div
                                                className={`transaction-field-withdraw ${
                                                    inputError ? 'error' : ''
                                                }`}
                                            >
                                                <label>Amount</label>
                                                <input
                                                    type="text"
                                                    value={bankInput}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter amount"
                                                    name="amt"
                                                />
                                            </div>
                                            <div
                                                className="withdrawal-balance-desktop
                          "
                                            >
                                                You have{' '}
                                                <span>₱{sg8Wallet}</span> in
                                                your wallet
                                            </div>
                                            <div
                                                className={`transaction-field-message ${alertType}`}
                                            >
                                                {alertMessage}
                                            </div>
                                            {rollover &&
                                                parseFloat(rollover.rollover) >
                                                    0 && (
                                                    <div className="transaction-field-notice-requirements">
                                                        <div>
                                                            <img
                                                                src="/images/icon/notice-icon.webp"
                                                                alt=""
                                                                className="notice-icon"
                                                            />
                                                            <strong>
                                                                You have{' '}
                                                                <span>
                                                                    {
                                                                        rollover.rollover
                                                                    }
                                                                </span>{' '}
                                                                incomplete
                                                                turnover
                                                                requirement
                                                            </strong>
                                                        </div>
                                                        <div>
                                                            Please contact our
                                                            customer support
                                                            before making your
                                                            next withdrawal.
                                                        </div>
                                                    </div>
                                                )}
                                            <div
                                                className={`transaction-field-message ${
                                                    inputError ? 'error' : ''
                                                }`}
                                            >
                                                <div>
                                                    {
                                                        'From ₱200 up to ₱50,000 per transaction. Contact support to request a higher limit.'
                                                    }
                                                </div>
                                            </div>{' '}
                                            {!isSubmitted && (
                                                <div className="deposit-submit-btn-container">
                                                    <button
                                                        type="submit"
                                                        className="withdraw-submit-btn"
                                                        disabled={
                                                            isSubmitDisabled ||
                                                            processing
                                                        }
                                                        style={
                                                            isSubmitDisabled ||
                                                            processing
                                                                ? {
                                                                      background:
                                                                          '#9c9c9c',
                                                                      cursor: 'not-allowed',
                                                                  }
                                                                : {}
                                                        }
                                                        onClick={(e) => {
                                                            if (processing) {
                                                                e.preventDefault();
                                                                return;
                                                            }
                                                        }}
                                                    >
                                                        {processing
                                                            ? 'Processing...'
                                                            : 'Continue Cashout'}
                                                    </button>
                                                </div>
                                            )}
                                        </form>
                                    </div>
                                )}

                            {activeSection === 'open_bank' &&
                                selectedBank &&
                                withdrawType === 'crypto' && (
                                    <>
                                        {/* <div className="form-wallet-balance">
                          Available Balance: <strong>{sg8Wallet}</strong>
                        </div> */}
                                        <PaymentSelection
                                            filterType={withdrawType}
                                            selected={selectedPayment?.id}
                                            onSelect={handlePaymentSelect}
                                            onAddNew={() =>
                                                handleChangeSection(
                                                    CHOOSE_PAYMENT
                                                )
                                            }
                                            methods={paymentMethods}
                                            onEditPayment={(method) => {
                                                setEditingMethod(method);
                                                setActiveSection(EDIT_PAYMENT);
                                            }}
                                        />
                                        <form
                                            onSubmit={handleEwalletSubmit}
                                            className="transaction-form"
                                            encType="multipart/form-data"
                                        >
                                            <input
                                                name="cmd"
                                                type="hidden"
                                                value={selectedBank?.cmd || ''}
                                            />

                                            {selectedBank?.name !==
                                                'TRC20-USDT' && (
                                                <input
                                                    name="method"
                                                    type="hidden"
                                                    value={
                                                        selectedBank?.method ||
                                                        ''
                                                    }
                                                />
                                            )}
                                            <input
                                                name="mbc"
                                                type="hidden"
                                                value={selectedBank?.mbc || ''}
                                            />
                                            <input
                                                name="man"
                                                type="hidden"
                                                value={selectedBank?.name || ''}
                                            />
                                            {/* <input name="man" type="hidden" value={accountName} /> */}
                                            <div
                                                className={`transaction-field-withdraw ${
                                                    inputError ? 'error' : ''
                                                }`}
                                                style={{ marginBottom: '10px' }}
                                            >
                                                <label>Wallet Address</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter wallet address"
                                                    name="mac"
                                                    value={walletAddress}
                                                    onChange={(e) =>
                                                        setWalletAddress(
                                                            e.target.value
                                                        )
                                                    }
                                                    readOnly
                                                />
                                            </div>
                                            <div
                                                className={`transaction-field-withdraw ${
                                                    inputError ? 'error' : ''
                                                }`}
                                            >
                                                <label>Amount</label>
                                                <input
                                                    type="text"
                                                    value={bankInput}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter amount"
                                                    name="amt"
                                                />
                                            </div>
                                            <div
                                                className={`transaction-field-message ${alertType}`}
                                            >
                                                {alertMessage}
                                            </div>
                                            <div
                                                className={`transaction-field-message ${
                                                    inputError ? 'error' : ''
                                                }`}
                                            >
                                                <div>
                                                    {
                                                        'From ₱200 up to ₱50,000 per transaction. Contact support to request a higher limit.'
                                                    }
                                                </div>
                                            </div>
                                            {!isSubmitted && (
                                                <div className="deposit-submit-btn-container">
                                                    <button
                                                        type="submit"
                                                        className="withdraw-submit-btn"
                                                        disabled={
                                                            isSubmitDisabled ||
                                                            processing
                                                        }
                                                        style={
                                                            isSubmitDisabled ||
                                                            processing
                                                                ? {
                                                                      background:
                                                                          '#9c9c9c',
                                                                      cursor: 'not-allowed',
                                                                  }
                                                                : {}
                                                        }
                                                        onClick={(e) => {
                                                            if (processing) {
                                                                e.preventDefault();
                                                                return;
                                                            }
                                                        }}
                                                    >
                                                        {processing
                                                            ? 'Processing...'
                                                            : 'Continue Cashout'}
                                                    </button>
                                                </div>
                                            )}
                                        </form>
                                    </>
                                )}

                            {activeSection === 'open_bank' &&
                                selectedBank &&
                                withdrawType === 'bank' && (
                                    <>
                                        {/* <div className="form-wallet-balance">
                          Available Balance: <strong>{sg8Wallet}</strong>
                        </div> */}
                                        <PaymentSelection
                                            filterType={withdrawType}
                                            selected={selectedPayment?.id}
                                            onSelect={handlePaymentSelect}
                                            onAddNew={() =>
                                                handleChangeSection(
                                                    CHOOSE_PAYMENT
                                                )
                                            }
                                            methods={paymentMethods}
                                            onEditPayment={(method) => {
                                                setEditingMethod(method);
                                                setActiveSection(EDIT_PAYMENT);
                                            }}
                                        />
                                        <form
                                            onSubmit={handleEwalletSubmit}
                                            className="transaction-form"
                                            encType="multipart/form-data"
                                        >
                                            <input
                                                name="cmd"
                                                type="hidden"
                                                value={selectedBank?.cmd || ''}
                                            />
                                            <input
                                                name="method"
                                                type="hidden"
                                                value={
                                                    selectedBank?.method || ''
                                                }
                                            />
                                            <input
                                                name="mbc"
                                                type="hidden"
                                                value={selectedBank?.mbc || ''}
                                            />
                                            <div
                                                className={`transaction-field-withdraw ${
                                                    inputError ? 'error' : ''
                                                }`}
                                                style={{
                                                    marginBottom: '10px',
                                                    // border: "1px solid #8eb000",
                                                }}
                                            >
                                                <label>Bank Account Name</label>
                                                <input
                                                    name="man"
                                                    type="text"
                                                    placeholder="Enter account name"
                                                    value={accountName}
                                                    onChange={(e) =>
                                                        setAccountName(
                                                            e.target.value
                                                        )
                                                    }
                                                    readOnly
                                                />
                                            </div>
                                            <div
                                                className={`transaction-field-withdraw ${
                                                    inputError ? 'error' : ''
                                                }`}
                                                style={{
                                                    marginBottom: '10px',
                                                    // border: "1px solid #8eb000",
                                                }}
                                            >
                                                <label>
                                                    Bank Account Number
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter account number"
                                                    name="mac"
                                                    value={accountNumber}
                                                    onChange={(e) =>
                                                        setAccountNumber(
                                                            e.target.value
                                                        )
                                                    }
                                                    readOnly
                                                />
                                            </div>
                                            <div
                                                className={`transaction-field-withdraw ${
                                                    inputError ? 'error' : ''
                                                }`}
                                            >
                                                <label>Amount</label>
                                                <input
                                                    type="text"
                                                    value={bankInput}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter amount"
                                                    name="amt"
                                                />
                                            </div>
                                            <div
                                                className="withdrawal-balance-desktop
                          "
                                            >
                                                You have{' '}
                                                <span>₱{sg8Wallet}</span> in
                                                your wallet
                                            </div>
                                            <div
                                                className={`transaction-field-message ${alertType}`}
                                            >
                                                {alertMessage}
                                            </div>
                                            {rollover &&
                                                parseFloat(rollover.rollover) >
                                                    0 && (
                                                    <div className="transaction-field-notice-requirements">
                                                        <div>
                                                            <img
                                                                src="/images/icon/notice-icon.webp"
                                                                alt=""
                                                                className="notice-icon"
                                                            />
                                                            <strong>
                                                                You have{' '}
                                                                <span>
                                                                    {
                                                                        rollover.rollover
                                                                    }
                                                                </span>{' '}
                                                                incomplete
                                                                turnover
                                                                requirement
                                                            </strong>
                                                        </div>
                                                        <div>
                                                            Please contact our
                                                            customer support
                                                            before making your
                                                            next withdrawal.
                                                        </div>
                                                    </div>
                                                )}
                                            <div
                                                className={`transaction-field-message ${
                                                    inputError ? 'error' : ''
                                                }`}
                                            >
                                                <div>
                                                    {
                                                        'From ₱100 up to ₱300,000 per transaction. Contact support to request a higher limit.'
                                                    }
                                                </div>
                                            </div>
                                            {!isSubmitted && (
                                                <div className="deposit-submit-btn-container">
                                                    <button
                                                        type="submit"
                                                        className="withdraw-submit-btn"
                                                        disabled={
                                                            isSubmitDisabled ||
                                                            processing
                                                        }
                                                        style={
                                                            isSubmitDisabled ||
                                                            processing
                                                                ? {
                                                                      background:
                                                                          '#9c9c9c',
                                                                      cursor: 'not-allowed',
                                                                  }
                                                                : {}
                                                        }
                                                        onClick={(e) => {
                                                            if (processing) {
                                                                e.preventDefault();
                                                                return;
                                                            }
                                                        }}
                                                    >
                                                        {processing
                                                            ? 'Processing...'
                                                            : 'Continue Cashout'}
                                                    </button>
                                                </div>
                                            )}
                                        </form>
                                    </>
                                )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
